import { forwardRef, useEffect, useImperativeHandle, useRef, type Dispatch, type SetStateAction } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { DocumentDirectoryType } from '@econtractor/types';
import { FilesCloudUpload } from 'modules/files';
import type { ProposalEditFormValues } from '../Proposal/types';
import { getProposalIsUahMode } from './model/helper/proposalPriceUtils';
import useProposalImages from './model/useProposalImages';
import useSettingsParams from 'hooks/useSettingsParams';
import { getCurrencyI18nParams } from 'utils/helpers/currency/currencyHelpers';

interface ProposalImageFieldProps {
    canEdit: boolean;
    loading: boolean;
    deleting: boolean;
    progress: number | boolean;
    files: File[];
    setFiles: Dispatch<SetStateAction<File[]>>;
    value: string;
    label: string;
    buttonLabel: string;
    currentLabel: string;
    removeLabel: string;
    uploadingLabel: string;
    altText: string;
    hint?: string;
    onRemove: () => Promise<void>;
}

const ProposalImageField = ({
    canEdit,
    loading,
    deleting,
    progress,
    files,
    setFiles,
    value,
    label,
    buttonLabel,
    currentLabel,
    removeLabel,
    uploadingLabel,
    altText,
    hint,
    onRemove,
}: ProposalImageFieldProps) => (
    <>
        <FilesCloudUpload
            disabled={!canEdit || loading}
            progress={progress}
            files={files}
            setFiles={(nextFiles) => {
                if (typeof nextFiles === 'function') {
                    setFiles((prev) => {
                        const next = nextFiles(prev);
                        return next.slice(-1);
                    });
                    return;
                }
                setFiles(nextFiles.slice(-1));
            }}
            label={label}
            buttonLabel={buttonLabel}
            uploadingLabel={uploadingLabel}
        />
        {hint ? <div className="small text-muted mt-1 mb-3">{hint}</div> : null}

        {value ? (
            <div className="mb-3">
                <div className="text-muted fs-12 mb-1">{currentLabel}</div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                    <img src={value} alt={altText} style={{ maxHeight: '64px', maxWidth: '100%', objectFit: 'contain' }} />
                    {canEdit ? (
                        <Button
                            type="button"
                            className="px-1 py-0"
                            variant="light-danger"
                            size="sm"
                            title={removeLabel}
                            aria-label={removeLabel}
                            disabled={loading || deleting || progress !== false}
                            onClick={() => void onRemove()}
                        >
                            {deleting ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <i className="bi bi-trash3 fs-12"></i>
                            )}
                        </Button>
                    ) : null}
                </div>
            </div>
        ) : null}
    </>
);

export interface ProposalGeneralTabHandle {
    uploadPendingImages: () => Promise<{ proposalLogo: string; proposalBackground: string }>;
}

export type ProposalGeneralTabMode = 'dir' | 'lead' | 'object';

const DOCUMENT_TYPE_OPTIONS: DocumentDirectoryType[] = ['contract', 'invoice', 'act', 'other'];
const PROPOSAL_NUMBER_MAX_LENGTH = 250;

const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';
    return DT.fromMillis(value).toISODate() || '';
};

const isoDateToMillis = (isoDate: string): number | null => {
    const raw = String(isoDate || '').trim();
    if (!raw) return null;
    const parsed = DT.fromISO(raw).startOf('day');
    return parsed.isValid ? parsed.toMillis() : null;
};

interface ProposalGeneralTabProps {
    mode: ProposalGeneralTabMode;
    formData: ProposalEditFormValues;
    rate: number;
    translationNs?: string;
    /** Show document type select (Documents directory only; hidden for commercial proposals). */
    showDocumentTypeSelect?: boolean;
    loading?: boolean;
    disabled?: boolean;
    isBackgroundFieldVisible?: boolean;
    showLogoField?: boolean;
    onChange: (patch: Partial<ProposalEditFormValues>) => void;
    onBusyChange?: (busy: boolean) => void;
}

const ProposalGeneralTab = forwardRef<ProposalGeneralTabHandle, ProposalGeneralTabProps>(({
    mode,
    formData,
    rate,
    translationNs = 'Proposal',
    showDocumentTypeSelect = false,
    loading = false,
    disabled = false,
    isBackgroundFieldVisible = false,
    showLogoField = true,
    onChange,
    onBusyChange,
}, ref) => {
    const { t } = useTranslation([translationNs, 'common']);
    const params = useSettingsParams();
    const canEdit = !disabled;
    const isUahMode = getProposalIsUahMode(rate);
    const showNameHint = mode === 'lead' || mode === 'object';
    const showDocumentMetaFields = mode === 'lead' || mode === 'object';
    const refInputName = useRef<HTMLInputElement>(null);
    const {
        logoFiles,
        setLogoFiles,
        logoProgress,
        deletingLogo,
        removeLogo,
        backgroundFiles,
        setBackgroundFiles,
        backgroundProgress,
        deletingBackground,
        removeBackground,
        isImageBusy,
        uploadPendingImages,
    } = useProposalImages({
        proposalLogo: formData.proposalLogo,
        proposalBackground: formData.proposalBackground,
        loading,
        onChange,
    });

    useImperativeHandle(ref, () => ({ uploadPendingImages }), [uploadPendingImages]);

    useEffect(() => {
        onBusyChange?.(isImageBusy);
    }, [isImageBusy, onBusyChange]);

    useEffect(() => {
        setTimeout(() => {
            if (refInputName.current) {
                refInputName.current.focus();
            }
        }, 100);
    }, [formData.proposalName]);

    return (
        <>
            {showDocumentTypeSelect ? (
                <Form.Group className="mb-3">
                    <Form.Label>{t('edit.fields.proposalType')}</Form.Label>
                    <Form.Select
                        disabled={!canEdit}
                        value={formData.proposalType === 'proposal' ? 'contract' : formData.proposalType}
                        onChange={(e) => onChange({ proposalType: e.target.value as DocumentDirectoryType })}
                    >
                        {DOCUMENT_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                                {t(`documentTypes.${type}`)}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            ) : null}

            <Form.Group className="mb-3">
                <Form.Label>{t('edit.fields.proposalName')}</Form.Label>
                <Form.Control
                    ref={refInputName}
                    disabled={!canEdit}
                    value={formData.proposalName}
                    onChange={(e) => onChange({ proposalName: e.target.value })}
                />
                {showNameHint ? (
                    <div className="small text-muted mt-1">{t('edit.fields.proposalNameHint')}</div>
                ) : null}
            </Form.Group>

            <Form.Group className="mb-0">
                <Form.Label>{t('edit.fields.proposalComment')}</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={5}
                    disabled={!canEdit}
                    value={formData.proposalComment}
                    maxLength={500}
                    onChange={(e) => onChange({ proposalComment: e.target.value.slice(0, 500) })}
                />
                <div className="small text-muted text-end mt-1">
                    {String(formData.proposalComment || '').length} / 500
                </div>
            </Form.Group>

            {showDocumentMetaFields ? (
                <Row className="mt-3 g-3 mb-3">
                    <Col xs={12} sm>
                        <Form.Group>
                            <Form.Label>{t('edit.fields.proposalNumber')}</Form.Label>
                            <Form.Control
                                disabled={!canEdit}
                                value={formData.proposalNumber}
                                maxLength={PROPOSAL_NUMBER_MAX_LENGTH}
                                onChange={(e) =>
                                    onChange({ proposalNumber: e.target.value.slice(0, PROPOSAL_NUMBER_MAX_LENGTH) })
                                }
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm="auto">
                        <Form.Group>
                            <Form.Label>{t('edit.fields.proposalDt')}</Form.Label>
                            <Form.Control
                                type="date"
                                disabled={!canEdit}
                                value={millisToIsoDate(formData.proposalDt)}
                                onChange={(e) => onChange({ proposalDt: isoDateToMillis(e.target.value) })}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            ) : null}

            <div className="form-check form-switch mt-3 mb-3">
                {!isUahMode && (
                    <>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="proposal-prices-in-usd"
                            checked={Boolean(formData.pricesInUsd)}
                            onChange={(e) => onChange({ pricesInUsd: Boolean(e.target.checked) })}
                            disabled={!canEdit || isImageBusy}
                        />
                        <label className="form-check-label" htmlFor="proposal-prices-in-usd">
                            {t('edit.general.usdLabel', getCurrencyI18nParams())}
                        </label>
                        <div className="small text-muted">
                            {formData.pricesInUsd
                                ? t('edit.general.usdHintUsd', getCurrencyI18nParams())
                                : t('edit.general.usdHintUah', {
                                      ...getCurrencyI18nParams(),
                                      rate: Number(rate || 0).toFixed(2),
                                  })}
                        </div>
                    </>
                )}
            </div>

            <div className="form-check form-switch mb-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="proposal-prices-with-vat"
                    checked={Boolean(formData.proposalVat)}
                    onChange={(e) => onChange({ proposalVat: Boolean(e.target.checked) })}
                    disabled={!canEdit || isImageBusy}
                />
                <label className="form-check-label" htmlFor="proposal-prices-with-vat">
                    {t('edit.general.vatLabel')}
                </label>
                <div className="small text-muted">
                    {formData.proposalVat
                        ? t('edit.general.vatHintOn', { vat: params.vatPercent })
                        : t('edit.general.vatHintOff')}
                </div>
            </div>

            {showLogoField ? (
                <ProposalImageField
                    canEdit={canEdit}
                    loading={loading}
                    deleting={deletingLogo}
                    progress={logoProgress}
                    files={logoFiles}
                    setFiles={setLogoFiles}
                    value={formData.proposalLogo}
                    label={t('edit.fields.proposalLogo')}
                    buttonLabel={t('edit.buttons.uploadLogo')}
                    uploadingLabel={t('common:buttons.uploading')}
                    currentLabel={t('edit.fields.currentLogo')}
                    removeLabel={t('edit.buttons.removeLogo')}
                    altText={t('edit.preview.logoAlt')}
                    hint={t('edit.fields.proposalLogoHint')}
                    onRemove={removeLogo}
                />
            ) : null}

            {isBackgroundFieldVisible ? (
                <ProposalImageField
                    canEdit={canEdit}
                    loading={loading}
                    deleting={deletingBackground}
                    progress={backgroundProgress}
                    files={backgroundFiles}
                    setFiles={setBackgroundFiles}
                    value={formData.proposalBackground}
                    label={t('edit.fields.proposalBackground')}
                    buttonLabel={t('edit.buttons.uploadBackground')}
                    uploadingLabel={t('common:buttons.uploading')}
                    currentLabel={t('edit.fields.currentBackground')}
                    removeLabel={t('edit.buttons.removeBackground')}
                    altText={t('edit.preview.backgroundAlt')}
                    onRemove={removeBackground}
                />
            ) : null}
        </>
    );
});

ProposalGeneralTab.displayName = 'ProposalGeneralTab';

export default ProposalGeneralTab;
